import Vue from "vue";

import UserService from '@/services/user.service.js';
import UserModel from '@/models/user.model';

import {
  USER_FETCH,
  USER_LOGIN,
  USER_LOGOUT,
  USER_FORGOT_PASSWORD,
  USER_CLEAR_MESSAGE,
  USER_SITE_MODE
} from "../actions.type";

import {
  USER_SET,
  USER_RESET,
  USER_PURGE,
  USER_SET_ERROR,
  USER_SET_MESSAGE,
  USER_SET_AUTH_STATE,
  USER_RESET_AUTH
} from "../mutations.type";

import {
  LOGIN_COOKIE_NAME,
  LOGIN_COOKIE_USERID,
  LOGIN_TOKEN_RANDOM,
  USER_INFO,
  USER_ADDRESS,
  USER_LOGGED_IN,
  ORDER_INTERNAL_SALE
} from "../cookie.keys";

const state = {
  has_error: null,
  message: "",
  user: null,
  is_authenticating: false,
  is_authenticated: false,
  hide_menu: false,
  hide_footer: false,
  site_mode: "customer"
};

const getters = {
  currentUser(state) {
    return state.user;
  },
  isAuthenticated(state) {
    return state.is_authenticated;
  },
  siteMode(state) {
    return state.site_mode;
  }
};

function mapUserResponseToModel(response) {
  Vue.$log.debug(response);
  
  var address = null;
  if(response.address) {
    var building = response.address.address_2 == "" ? "" : response.address.address_1;
    var combined = building == "" ? response.address.address_1 : response.address.address_2;
    address = {
      country: response.address.country,
      country_code: response.address.iso_code_3,
      code: response.address.postcode,
      city: response.address.city,
      province: response.address.zone,
      province_code: response.address.zone_code,
      area: response.address.area,
      building: building,
      combined_number_street: combined,
      street_number: "",
      street: combined,
      instructions: response.address.delivery_instructions
    };
  }

  var user = new UserModel({
    id: response.data.customer_id || 0,
    status: parseInt(response.data.status),
    is_approved: parseInt(response.data.approved) > 0,
    date_added: response.data.date_added || null,
    email_address: response.data.email,
    first_name: response.data.firstname,
    last_name: response.data.lastname,
    mobile_number: response.data.telephone,
    id_number: response.data.id_number,
    id_type: response.data.id_type,
    session: response.data.session,
    token: response.data.token,
    address: address
  });
  
  return user;
}

function setUserCookies(data) {
  Vue.$log.debug(data);

  Vue.cookie.set(LOGIN_COOKIE_NAME, data.data.session, 1);  
  Vue.cookie.set(LOGIN_COOKIE_USERID, data.data.customer_id, 1);  
  Vue.cookie.set(LOGIN_TOKEN_RANDOM, data.data.token, 1);
  Vue.cookie.set(USER_INFO, JSON.stringify({'name':data.data.firstname,'surname':data.data.lastname,'email':data.data.email,'phone':data.data.telephone}), 1);
  Vue.cookie.set(USER_ADDRESS, JSON.stringify(data.address), 1);
  Vue.cookie.set(USER_LOGGED_IN, true, 1);

  Vue.cookie.delete(ORDER_INTERNAL_SALE);
}

function clearCookies() {
  Vue.cookie.delete(LOGIN_COOKIE_NAME);
  Vue.cookie.delete(LOGIN_COOKIE_USERID);
  Vue.cookie.delete(LOGIN_TOKEN_RANDOM);
  Vue.cookie.delete(USER_INFO);
  Vue.cookie.delete(USER_ADDRESS);
  Vue.cookie.delete(USER_LOGGED_IN);
  Vue.cookie.delete(ORDER_INTERNAL_SALE);
}

const actions = {
  [USER_SITE_MODE](context, site_mode) {
    context.commit(USER_SITE_MODE, { site_mode: site_mode });
  },
  [USER_FETCH](context) {
    var isLoggedIn = Vue.cookie.get(USER_LOGGED_IN);
    context.commit(USER_SET_AUTH_STATE, { authenticated: isLoggedIn });
    if(isLoggedIn) {      
      context.commit(USER_SET, UserService.getLocal())
    } else {
      UserService.deleteLocal();
    }    
  },
  [USER_LOGIN](context, credentials) {
    context.commit(USER_RESET, { authenticating: true, authenticated: false });
    Vue.$log.debug("User Login", credentials);
    return UserService.login(credentials.username, credentials.password)
      .then(({
        data
      }) => {
        if (data.success) {
          Vue.$log.debug("User Logged In", data);
          var user = mapUserResponseToModel(data);
          Vue.$log.debug("User set local", user);
          UserService.setLocal(user)
          context.commit(USER_SET, user);
          context.commit(USER_SET_MESSAGE);
          context.commit(USER_SET_AUTH_STATE, { authenticating: false, authenticated: true });
          setUserCookies(data);
          if(credentials.redirect) {
            window.location = credentials.redirect;
          }
        } else {
          context.commit(USER_SET_MESSAGE, data.error.warning);
          context.commit(USER_SET_AUTH_STATE, { authenticating: false, authenticated: false });
          context.commit(USER_SET_ERROR, true);
          Vue.$log.warn("ERROR Logging in", data);
        }
      })
      .catch(({
        response
      }) => {
        context.commit(USER_SET_AUTH_STATE, { authenticating: false, authenticated: false });
        context.commit(USER_SET_ERROR, true);
        Vue.$log.fatal("ERROR Logging in", response);
      });
  },
  [USER_LOGOUT](context) {
    return UserService.logout()
      .then(({
        data
      }) => {
        Vue.$log.debug(data);
        clearCookies();
        context.commit(USER_PURGE);
      })
      .catch(({
        response
      }) => {
        context.commit(USER_SET_ERROR, true);
        Vue.$log.fatal("ERROR Logging out", response);
      });
  },
  [USER_CLEAR_MESSAGE](context) {
    context.commit(USER_SET_MESSAGE, "");
  },
  [USER_FORGOT_PASSWORD](context, credentials) {
    context.commit(USER_RESET, { authenticating: true });
    return UserService.forgotPassword(credentials.username)
      .then(({
        data
      }) => {
        Vue.$log.debug(data);
        if(!data.success) context.commit(USER_SET_ERROR, true);
        context.commit(USER_SET_MESSAGE, data.message);
        context.commit(USER_SET_AUTH_STATE, { authenticating: false });
      })
      .catch(({
        response
      }) => {
        context.commit(USER_SET_AUTH_STATE, { authenticating: false });
        context.commit(USER_SET_ERROR, true);
        Vue.$log.fatal("ERROR Forgot Password", response);
      });
  }
};

const mutations = {
  [USER_SITE_MODE](state, mode) {
    state.site_mode = mode.site_mode;
  },
  [USER_RESET_AUTH](state, status) {
    state.is_authenticating = status.authenticating;    
    state.is_authenticated = status.authenticated;    
    state.has_error = false;
    state.message = "";
  },
  [USER_SET_ERROR](state, has_error) {
    state.has_error = has_error;
  },
  [USER_SET](state, user) {
    state.is_authenticated = true;
    state.user = user;
    state.has_error = false;
  },
  [USER_PURGE](state) {
    state.is_authenticated = false;
    state.user = {};
    state.has_error = false;
  },
  [USER_SET_AUTH_STATE](state, status) {    
    state.is_authenticating = status.authenticating;    
    state.is_authenticated = status.authenticated;    
  },
  [USER_RESET](state, status) {    
    state.is_authenticating = status.authenticating;    
    state.is_authenticated = status.authenticated;
    state.has_error = false;
    state.user = {};
    state.message = "";    
  },
  [USER_SET_MESSAGE](state, message) {
    state.message = message;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
}